/**
 * THE LOD-0 COLUMN CLIP OVER THE FOOTPRINT A CHUNK MESHES, not only the one it stores.
 *
 * A chunk marches GRID_SIZE cells: its own CHUNK_SIZE voxel columns plus MESH_MARGIN borrowed from
 * its +X / +Z / +XZ neighbours, and SurfaceNets gives it sole ownership of the band standing in
 * those borrowed columns (the high side suppresses every face at a cell-0 layer). So the chunks a
 * column must hold are decided by the surface over that wider footprint: a building wall standing in
 * the neighbour's first two voxel rows is drawn by THIS column's chunk at that height, even when the
 * chunk itself is pure air. The coarse clip scans GRID_SIZE² for this reason
 * (TerrainGenerator.generateLodColumn); a tile only carries its own 32×32, so the lod-0 mirror is
 * assembled here from the neighbours' tiles as they arrive: each tile publishes the extremes of its
 * LOW border columns (what the chunk below it on each axis borrows), and a column's clip is its own
 * range widened by the three borders it reads.
 */

import { CHUNK_SIZE, MAP_TILE_SIZE, MESH_MARGIN, tilePixelIndex } from '@worldify/shared';

export interface ChunkRange { minCy: number; maxCy: number }
export interface HeightExtremes { min: number; max: number }

/** The low border a −X / −Z / −XZ neighbour borrows from this tile: extremes over its first
 *  MESH_MARGIN columns along X, its first MESH_MARGIN rows along Z, and their corner. */
export interface BorrowedBorders { x: HeightExtremes; z: HeightExtremes; xz: HeightExtremes }

/**
 * Chunk-Y range for a height span. maxCy from max + 1: the top face of the highest solid voxel is
 * meshed from the voxel above it, so a top flush with a chunk's last row needs the next chunk for
 * its air margin. minCy is the chunk of the lowest surface point.
 */
export function rangeOfHeights(minHeight: number, maxHeight: number): ChunkRange {
  return {
    minCy: Math.floor(minHeight / CHUNK_SIZE),
    maxCy: Math.floor((maxHeight + 1) / CHUNK_SIZE),
  };
}

export function borrowedBorders(heights: ArrayLike<number>): BorrowedBorders {
  const x: HeightExtremes = { min: Infinity, max: -Infinity };
  const z: HeightExtremes = { min: Infinity, max: -Infinity };
  const xz: HeightExtremes = { min: Infinity, max: -Infinity };
  for (let lz = 0; lz < MAP_TILE_SIZE; lz++) {
    for (let lx = 0; lx < MESH_MARGIN; lx++) {
      const h = heights[tilePixelIndex(lx, lz)];
      if (h < x.min) x.min = h;
      if (h > x.max) x.max = h;
      if (lz < MESH_MARGIN) {
        if (h < xz.min) xz.min = h;
        if (h > xz.max) xz.max = h;
      }
    }
  }
  for (let lz = 0; lz < MESH_MARGIN; lz++) {
    for (let lx = 0; lx < MAP_TILE_SIZE; lx++) {
      const h = heights[tilePixelIndex(lx, lz)];
      if (h < z.min) z.min = h;
      if (h > z.max) z.max = h;
    }
  }
  return { x, z, xz };
}

/** A column's clip: its own range, widened by what it borrows from each known neighbour. */
export function widenedColumnRange(
  own: ChunkRange,
  plusX: BorrowedBorders | undefined,
  plusZ: BorrowedBorders | undefined,
  plusXZ: BorrowedBorders | undefined,
): ChunkRange {
  let { minCy, maxCy } = own;
  const take = (e: HeightExtremes | undefined): void => {
    if (!e || e.min > e.max) return;
    const r = rangeOfHeights(e.min, e.max);
    if (r.minCy < minCy) minCy = r.minCy;
    if (r.maxCy > maxCy) maxCy = r.maxCy;
  };
  take(plusX?.x);
  take(plusZ?.z);
  take(plusXZ?.xz);
  return { minCy, maxCy };
}
